import React, { useCallback, useState } from 'react';
import { queryCache } from 'react-query';
import { Poppins } from 'src/common';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { selectUserOption, pluralize } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import { EventCsvHeaders } from 'src/api/types/misc';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import {
  EventInput,
  EventCompletionStatus,
  EventKind,
  ObjectKind,
  useUpsertEventMutation,
} from 'src/graphql/_generated';
import { convertDateToISO, delimiters, ParseResult, validateRow, ValidationErrorItem } from 'src/utils/csv';
import { useCache } from 'src/graphql';
import { EventCSV, generateCsvTemplateLink, pathNames } from './util';
import { validationSchema } from '../../util';
import CSelect from 'src/components/form/Select';
import { eventCompletionStatusOptions } from 'src/api/types/options';
import {
  CsvDropzoneWithTemplate,
  CsvValidationErrors,
  CsvUploadButtons,
  SCVSummary,
  DzoneWrap,
} from 'src/components/CsvUpload';

const pluralizeActions = (count: number) => pluralize({ count, singular: 'action', plural: 'actions' });

interface FromCSVProps {
  onClose: () => void;
  setIsCsvDirty: (isDirty: boolean) => void;
  objectType: ObjectKind;
  objectId: string;
  state: {
    file: File | null;
    setFile: (file: File | null) => void;
    readyForUpload: boolean;
    setReadyForUpload: (ready: boolean) => void;
  };
  eventIds: string[];
}

export const FromCSV: React.FC<FromCSVProps> = ({ onClose, setIsCsvDirty, objectType, objectId, state, eventIds }) => {
  const { usersOptios, workspaceId } = useAuth();
  const [error, setError] = useState('');
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [validEvents, setValidEvents] = useState<EventInput[]>([]);
  const { refetchQuery } = useCache();

  const { file, setFile, setReadyForUpload } = state;

  const [upsertEvent, { loading }] = useUpsertEventMutation();

  const parseCSVToJSON = useCallback(
    (file: File) => {
      const tryParse = (delimiter: string) =>
        new Promise<ParseResult<EventCSV>>((resolve, reject) => {
          Papa.parse(file, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true,
            delimiter,
            complete: resolve,
            error: reject,
          });
        });

      (async () => {
        let result: ParseResult<EventCSV> | null = null;

        for (const delim of delimiters) {
          const parsed = await tryParse(delim);
          if (parsed.data && Object.keys(parsed.data[0] || {}).length > 2) {
            result = parsed;
            break;
          }
        }

        if (!result || !result.data || result.data.length === 0) {
          setValidationErrors([
            { rowIndex: 0, errors: [{ path: 'Data', message: 'No action data found in the file.' }] },
          ]);
          return;
        }

        if (result.data.length > 60) {
          setError('The number of actions exceeds the limit of 60.');
          setFile(null);
          return;
        }

        const mappedEvents: EventInput[] = result.data.map((el) => {
          const mapped = {
            id: el[EventCsvHeaders.ID],
            notes: el[EventCsvHeaders.Title] || '',
            ...(el[EventCsvHeaders.Owner] && { assignedTo: { text: el[EventCsvHeaders.Owner] } }),
            ...(el[EventCsvHeaders.DueDate] && {
              dueDate: convertDateToISO(el[EventCsvHeaders.DueDate]),
            }),
            completionStatus:
              el[EventCsvHeaders.CompletionStatus] === 'Completed'
                ? EventCompletionStatus.Done
                : EventCompletionStatus.Open,
            completedAt: el[EventCsvHeaders.DateCompleted] ? convertDateToISO(el[EventCsvHeaders.DateCompleted]) : null,
            ...(el[EventCsvHeaders.CompletedBy] && {
              completedBy: { text: el[EventCsvHeaders.CompletedBy] },
            }),
          };

          return mapped;
        });

        const promises = mappedEvents.map((row, idx) =>
          validateRow({ row, rowIndex: idx + 1, schema: validationSchema, pathNames }),
        );
        const results = await Promise.all(promises);
        const errorLog = results.filter((el) => el?.errors?.length) as ValidationErrorItem[];

        if (errorLog.length) {
          setValidationErrors(errorLog);
          setIsUploadSuccessful(false);
          setReadyForUpload(false);
        } else {
          setValidEvents(mappedEvents);
          setValidationErrors([]);
          setError('');
          setReadyForUpload(true);
        }
      })();
    },
    [setFile, setReadyForUpload],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      mpEvent(MPEvents.FileLoaded, {
        modal: 'Add Actions modal',
        tags: ['ACTIONS'],
      });
      if (acceptedFiles?.length > 0) {
        const selectedFile = acceptedFiles[0];
        setFile(selectedFile);
        setError('');
        setIsUploadSuccessful(false);
        setValidationErrors([]);
        parseCSVToJSON(selectedFile);
        setIsCsvDirty(true);
      }
    },
    [parseCSVToJSON, setFile, setIsCsvDirty],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
  });

  const save = async () => {
    mpEvent(MPEvents.ButtonClick, {
      button: 'Upload',
      modal: 'Add Actions modal',
      tags: ['ACTIONS'],
    });

    setReadyForUpload(false);

    try {
      const eventData: EventInput[] = validEvents.map((el) => {
        const { userAdd: assignedToUser } = selectUserOption({
          usersOptios,
          selectedUser: el.assignedTo,
        });

        const { userAdd: completedByUser } = selectUserOption({
          usersOptios,
          selectedUser: el.completedBy,
        });

        let completedAt = el.completedAt;
        if (el.completionStatus === EventCompletionStatus.Done && !completedAt) {
          completedAt = new Date().toISOString();
        }

        return {
          id: el.id,
          eventType: EventKind.Action,
          notes: el.notes,
          assignedTo: assignedToUser,
          dueDate: el.dueDate,
          objectType,
          objectId,
          completionStatus: el.completionStatus,
          completedAt: completedAt,
          completedBy: completedByUser,
        };
      });

      await upsertEvent({
        variables: { workspaceId, eventData },
      });

      queryCache.invalidateQueries();
      refetchQuery({ include: ['events'] });

      setIsUploadSuccessful(true);
      setIsCsvDirty(false);
      setReadyForUpload(false);
    } catch (error) {
      setError(`Error uploading actions: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setReadyForUpload(true);
    }
  };

  const resetUpload = () => {
    setIsUploadSuccessful(false);
    setValidationErrors([]);
    setError('');
    setFile(null);
    setReadyForUpload(false);
  };

  const newCount = validEvents.filter((e) => !e.id || !eventIds.includes(e.id)).length;
  const updatedCount = validEvents.filter((e) => e.id && eventIds.includes(e.id)).length;

  return (
    <DzoneWrap className="h-padding">
      <CsvDropzoneWithTemplate
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isDragActive={isDragActive}
        file={file}
        templateLink={generateCsvTemplateLink()}
        templateFileName="actions_template.csv"
        onTemplateClick={() =>
          mpEvent(MPEvents.DownloadCSVTemplate, {
            modal: 'Add Actions modal',
            tags: ['ACTIONS'],
          })
        }
      />

      <CsvValidationErrors errors={validationErrors} />

      {file && (
        <>
          {!isUploadSuccessful && (
            <SCVSummary
              items={[
                'File validated',
                `${updatedCount} ${pluralizeActions(updatedCount)} ready to be updated`,
                `${newCount} ${pluralizeActions(newCount)} ready to be created`,
              ]}
            />
          )}
          {isUploadSuccessful && (
            <SCVSummary
              isCompleted
              items={[
                `${updatedCount} existing ${pluralizeActions(updatedCount)} updated`,
                `${newCount} new ${pluralizeActions(newCount)} added`,
                'File upload successful',
              ]}
            />
          )}
        </>
      )}

      <CsvUploadButtons
        isUploadSuccessful={isUploadSuccessful}
        hasFile={!!file}
        isDisabled={!file || !!validationErrors.length || loading}
        onCancel={onClose}
        onUpload={save}
        onSelectFile={open}
        onUploadAnother={resetUpload}
        onDone={onClose}
      />

      {!file && (
        <>
          <Poppins px={14} weight={500}>
            The valid values of Completion Status are listed in the drop down below. Blank is also valid.
          </Poppins>
          <div className="grid-list grid-list--3">
            <CSelect
              value={null}
              placeholder="COMPLETION STATUS"
              menuPlacement="top"
              options={eventCompletionStatusOptions}
            />
          </div>
        </>
      )}

      {error && <div className="error">{error}</div>}
    </DzoneWrap>
  );
};
